import {Stack, Text, Title} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import {parseSDF} from 'bulk-import/SpreadsheetBulkImportDAO';
import SpreadsheetUploadPane from 'spreadsheet-import-wizard/shared/SpreadsheetUploadPane';
import type {SpreadsheetUploadResult} from 'spreadsheet-import-wizard/shared/SpreadsheetUploadPane';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import * as ImportUtil from 'util/ImportUtil';

import type {ModalAction, ModalState} from './moleculeImportReducer';
import SpreadsheetExampleTable from './SpreadsheetExampleTable';
import useMoleculeSchemasPairing from './useMoleculeSchemasPairing';

const SDF_EXTENSIONS = ['.sdf', '.sd'];

function isSdfFile(file: File): boolean {
  const lower = file.name.toLowerCase();
  return SDF_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

interface FileUploadPaneProps {
  state: ModalState;
  dispatch: React.Dispatch<ModalAction>;
}

export default function FileUploadPane({state, dispatch}: FileUploadPaneProps): JSX.Element {
  const [saltsBannerDismissed, setSaltsBannerDismissed] = useState(false);
  const {pairingMap} = useMoleculeSchemasPairing(null);
  const pairing = state.schemaId ? pairingMap[state.schemaId] : undefined;

  const handleSdfUpload = useCallback(
    async (file: File): Promise<{s3Url: string; headers: Array<string>}> => {
      const s3Url = (await ImportUtil.upload(file))!;
      const result = (await parseSDF(file.name, s3Url))!;
      return {s3Url: result.updatedUrl, headers: result.columnNames};
    },
    []
  );

  const handleUploadComplete = useCallback(
    (result: SpreadsheetUploadResult) => {
      dispatch({
        type: 'SET_FILE_UPLOAD_RESULT',
        payload: {file: result.file, s3Url: result.s3Url, headers: result.headers},
      });
    },
    [dispatch]
  );

  const handleRemove = useCallback(() => {
    dispatch({type: 'CLEAR_FILE_UPLOAD'});
  }, [dispatch]);

  return (
    <Stack spacing="lg">
      <Stack spacing="xxs">
        {/* m={0} neutralizes the global Bootstrap <h5> margin so the Stack's xxs gap governs spacing. */}
        <Title order={5} m={0}>
          Upload file
        </Title>
        <Text size="md">
          Upload a CSV, Excel, or SDF file to import into Benchling (example below). The first row should
          specify column headers, which must be unique. Each row after that should specify an individual
          molecule.
        </Text>
      </Stack>

      {!saltsBannerDismissed && (
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text="For files containing molecule with salts and solvents, ensure the salts and solvents are included in the molecules' SMILES string."
          onDismiss={() => {
            setSaltsBannerDismissed(true);
          }}
        />
      )}

      <SpreadsheetExampleTable schemaId={state.schemaId} pairing={pairing} />

      <SpreadsheetUploadPane
        accept=".csv,.xls,.xlsx,.sdf,.sd"
        title={null}
        customFileHandler={{shouldHandle: isSdfFile, handle: handleSdfUpload}}
        onUploadComplete={handleUploadComplete}
        onRemove={handleRemove}
        localeDescription="Tell us how numbers and dates are formatted in your file."
        uploadedFile={state.file}
        uploadedHeaders={state.headers}
      />
    </Stack>
  );
}
